export interface ScreenRecorderOptions {
  maxDurationMs?: number;
  withAudio?: boolean;
  onStop?: (file: File) => void;
  onError?: (err: Error) => void;
  onTick?: (elapsedMs: number) => void;
}

const PREFERRED_MIME_TYPES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp8",
  "video/webm",
  "video/mp4",
];

const pickMimeType = (): string | undefined => {
  if (typeof MediaRecorder === "undefined") return undefined;
  for (const t of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return undefined;
};

const extensionForMime = (mime: string): string => {
  if (mime.includes("webm")) return "webm";
  if (mime.includes("mp4")) return "mp4";
  return "bin";
};

export class ScreenRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private stopTimer: number | null = null;
  private tickTimer: number | null = null;

  constructor(private opts: ScreenRecorderOptions = {}) {}

  static isSupported(): boolean {
    return (
      typeof MediaRecorder !== "undefined" &&
      !!navigator.mediaDevices?.getDisplayMedia &&
      pickMimeType() !== undefined
    );
  }

  async start(): Promise<void> {
    if (this.recorder) throw new Error("already recording");

    const mime = pickMimeType();
    if (!mime) throw new Error("no supported video MIME type in this browser");

    this.stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: this.opts.withAudio ?? false,
    });

    // User clicked the browser's "Stop sharing" UI → finalize.
    this.stream.getVideoTracks()[0]?.addEventListener("ended", () => this.stop());

    this.recorder = new MediaRecorder(this.stream, { mimeType: mime });
    this.chunks = [];

    this.recorder.addEventListener("dataavailable", (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    });

    this.recorder.addEventListener("stop", () => {
      this.cleanupTimers();
      try {
        const blob = new Blob(this.chunks, { type: mime });
        const file = new File(
          [blob],
          `screen-${Date.now()}.${extensionForMime(mime)}`,
          { type: mime },
        );
        this.opts.onStop?.(file);
      } catch (err) {
        this.opts.onError?.(err as Error);
      } finally {
        this.stream?.getTracks().forEach((t) => t.stop());
        this.stream = null;
        this.recorder = null;
      }
    });

    this.recorder.addEventListener("error", (e) => {
      this.opts.onError?.((e as ErrorEvent).error ?? new Error("MediaRecorder error"));
    });

    this.recorder.start(500);
    this.startedAt = performance.now();

    const max = this.opts.maxDurationMs ?? 120_000;
    this.stopTimer = window.setTimeout(() => this.stop(), max);

    if (this.opts.onTick) {
      this.tickTimer = window.setInterval(() => {
        this.opts.onTick?.(performance.now() - this.startedAt);
      }, 200);
    }
  }

  stop(): void {
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.stop();
    }
  }

  isRecording(): boolean {
    return this.recorder?.state === "recording";
  }

  private cleanupTimers() {
    if (this.stopTimer !== null) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }
}
